import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { PathwayOption } from "./types/pathway.types";
import { pathwayExplorerClient } from "../../services/PathwayExplorerClient";
import { getApiErrorMessage } from "../../utils/apiErrorUtils";
import { ComparisonStatistics } from "./ComparisonStatistics";
import { ComparisonNetworkGraph } from "./ComparisonNetworkGraph";
import { useTheme } from "../../theme/useTheme";

export interface ComparisonResult {
  /** pathway value -> list of gene symbols */
  genesPerPathway: Record<string, string[]>;
  /** pathway value -> display label */
  pathwayLabels: Record<string, string>;
  /** unique union of all genes */
  comparisonGeneList: string[];
  /** one-hop gene-gene edges (deduped) */
  oneHopEdges: { source: string; target: string }[];
  /** genes that appear in >= 2 pathways */
  sharedGenes: Set<string>;
}

interface PathwayComparisonProps {
  pathwayOptions: PathwayOption[];
  selectedPathway: string;
  datasetId: string;
  edgeType: string;
  minEdgeScore: number;
}

function pathsToOneHopEdges(
  edges: { source: string; target: string; path: string[] }[]
): { source: string; target: string }[] {
  const seen = new Set<string>();
  const result: { source: string; target: string }[] = [];
  for (const e of edges) {
    const path = Array.isArray(e.path) ? e.path : [];
    if (path.length < 2) {
      const s = (e.source ?? "").trim();
      const t = (e.target ?? "").trim();
      if (s && t && s !== t) {
        const key = s < t ? `${s}\t${t}` : `${t}\t${s}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({ source: s, target: t });
        }
      }
      continue;
    }
    for (let i = 0; i < path.length - 1; i++) {
      const a = String(path[i]).trim();
      const b = String(path[i + 1]).trim();
      if (!a || !b || a === b) continue;
      const key = a < b ? `${a}\t${b}` : `${b}\t${a}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ source: a, target: b });
      }
    }
  }
  return result;
}

export const PathwayComparison: React.FC<PathwayComparisonProps> = ({
  pathwayOptions,
  selectedPathway,
  datasetId,
  edgeType,
  minEdgeScore,
}) => {
  const { mode } = useTheme();
  const isDark = mode === "dark";

  const [comparisonPathways, setComparisonPathways] = useState<string[]>([]);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [comparisonViewMode, setComparisonViewMode] = useState<"graph" | "statistics">("graph");
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);
  const comparisonResultsRef = useRef<HTMLDivElement>(null);

  // After Compare pathways succeeds, scroll and focus the results region
  useEffect(() => {
    if (!comparisonResult) return;
    const timer = window.setTimeout(() => {
      const el = comparisonResultsRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.focus({ preventScroll: true });
    }, 200);
    return () => clearTimeout(timer);
  }, [comparisonResult]);

  // Auto-select current pathway when it is set and available in options
  useEffect(() => {
    if (!selectedPathway) return;
    const inOptions = pathwayOptions.some((o) => o.value === selectedPathway);
    if (!inOptions) return;
    setComparisonPathways((prev) =>
      prev.includes(selectedPathway) ? prev : [selectedPathway, ...prev]
    );
  }, [selectedPathway, pathwayOptions]);

  const selectedOptions = useMemo(
    () => pathwayOptions.filter((o) => comparisonPathways.includes(o.value)),
    [pathwayOptions, comparisonPathways]
  );

  const count = comparisonPathways.length;
  const canCompare = count >= 2;

  const runComparison = useCallback(async () => {
    if (!canCompare || comparisonPathways.length < 2 || !datasetId) return;
    setIsComparing(true);
    setComparisonError(null);
    setComparisonResult(null);
    try {
      // (1) Get genes per pathway via API
      const genesPerPathway: Record<string, string[]> = {};
      const pathwayLabels: Record<string, string> = {};
      for (const pathwayValue of comparisonPathways) {
        const label = pathwayOptions.find((o) => o.value === pathwayValue)?.label ?? pathwayValue;
        pathwayLabels[pathwayValue] = label;
        const raw = await pathwayExplorerClient.getPathwayGenes({
          dataset: datasetId,
          pathway: pathwayValue,
        });
        const genes = (Array.isArray(raw) ? raw : []).map((g: unknown) =>
          typeof g === "string" ? g : (g as { gene_symbol?: string })?.gene_symbol ?? ""
        ).filter(Boolean) as string[];
        genesPerPathway[pathwayValue] = [...new Set(genes)];
      }

      // (2) Build union gene list and get one-hop neighbors
      const allGenes = Object.values(genesPerPathway).flat();
      const comparisonGeneList = [...new Set(allGenes)];

      if (comparisonGeneList.length < 2) {
        setComparisonViewMode("graph");
        setComparisonResult({
          genesPerPathway,
          pathwayLabels,
          comparisonGeneList,
          oneHopEdges: [],
          sharedGenes: new Set(),
        });
        return;
      }

      const kHopData = await pathwayExplorerClient.getKHopNeighbors({
        edgetype: edgeType,
        selected_genes: comparisonGeneList,
        k: 1,
        edge_threshold: minEdgeScore,
      });

      // (3) Convert paths to one-hop edges
      const oneHopEdges = pathsToOneHopEdges(
        (Array.isArray(kHopData) ? kHopData : []).map((e: { source?: string; target?: string; path?: string[] }) => ({
          source: e.source ?? "",
          target: e.target ?? "",
          path: Array.isArray(e.path) ? e.path : [],
        }))
      );

      // Identify shared genes (in >= 2 pathways)
      const pathwayCountByGene = new Map<string, number>();
      for (const genes of Object.values(genesPerPathway)) {
        for (const g of new Set(genes)) {
          pathwayCountByGene.set(g, (pathwayCountByGene.get(g) ?? 0) + 1);
        }
      }
      const sharedGenes = new Set<string>();
      pathwayCountByGene.forEach((c, gene) => {
        if (c >= 2) sharedGenes.add(gene);
      });

      setComparisonViewMode("graph");
      setComparisonResult({
        genesPerPathway,
        pathwayLabels,
        comparisonGeneList,
        oneHopEdges,
        sharedGenes,
      });
    } catch (error) {
      setComparisonError(getApiErrorMessage(error));
    } finally {
      setIsComparing(false);
    }
  }, [
    canCompare,
    comparisonPathways,
    datasetId,
    edgeType,
    minEdgeScore,
    pathwayOptions,
  ]);

  return (
    <div className="space-y-4 p-6">
      <div className="mb-4">
        <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
          <p className="text-xs md:text-sm text-gray-700 dark:text-gray-200">
            Compare multiple pathways side by side to analyze differences in gene interactions and
            pathway structures.
          </p>
        </div>
      </div>

      {pathwayOptions.length > 0 && (
        <div className="space-y-3">
          <Autocomplete
            multiple
            options={pathwayOptions}
            value={selectedOptions}
            onChange={(_, newValue) => {
              setComparisonPathways(newValue.map((o) => o.value));
              setComparisonResult(null);
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            filterSelectedOptions
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select at least two pathways to compare"
                placeholder="Search pathways..."
                variant="outlined"
              />
            )}
            renderOption={(props, option) => {
              const { className: muiClass, ...rest } = props;
              const rowBase = isDark
                ? "px-3 py-2 text-sm transition-colors border-b border-slate-600 last:border-b-0 bg-slate-900 text-slate-100 hover:bg-slate-800"
                : "px-3 py-2 text-sm transition-colors border-b border-gray-100 last:border-b-0 bg-white text-gray-900 hover:bg-gray-50";
              const rowSelected = props["aria-selected"]
                ? isDark
                  ? " !bg-slate-800 !text-blue-200"
                  : " !bg-blue-50 !text-blue-800"
                : "";
              return (
                <li {...rest} className={`${muiClass ?? ""} ${rowBase}${rowSelected}`.trim()}>
                  {option.label}
                </li>
              );
            }}
            componentsProps={{
              paper: {
                sx: isDark
                  ? {
                      bgcolor: "#0f172a",
                      color: "#f1f5f9",
                      border: "1px solid #64748b",
                      backgroundImage: "none",
                      boxShadow:
                        "0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.3)",
                    }
                  : {
                      bgcolor: "#fff",
                      color: "#0f172a",
                      border: "1px solid #e5e7eb",
                    },
              },
            }}
            sx={{
              width: "100%",
              ...(isDark && {
                // Labels (MUI default uses dark text; override with !important)
                "& .MuiInputLabel-root": {
                  color: "#e2e8f0 !important",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#93c5fd !important",
                },
                "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                  color: "#cbd5e1 !important",
                },
                // Outlined field: visible edge on slate-800 cards
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#1e293b !important",
                  color: "#f8fafc !important",
                  "& fieldset": {
                    borderColor: "#64748b !important",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#94a3b8 !important",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#60a5fa !important",
                  },
                },
                "& .MuiAutocomplete-inputRoot": {
                  color: "#f8fafc !important",
                },
                "& .MuiOutlinedInput-input": {
                  color: "#f8fafc !important",
                  WebkitTextFillColor: "#f8fafc",
                },
                "& input.MuiAutocomplete-input": {
                  color: "#f8fafc !important",
                  WebkitTextFillColor: "#f8fafc",
                },
                "& input::placeholder": {
                  color: "#94a3b8 !important",
                  opacity: "1 !important",
                },
                "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
                  color: "#cbd5e1 !important",
                },
                // Multi-select chips
                "& .MuiChip-root": {
                  backgroundColor: "#475569 !important",
                  border: "1px solid #94a3b8 !important",
                },
                "& .MuiChip-label": {
                  color: "#f8fafc !important",
                },
                "& .MuiChip-deleteIcon": {
                  color: "#e2e8f0 !important",
                  "&:hover": {
                    color: "#ffffff !important",
                  },
                },
              }),
            }}
          />

          {comparisonError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {comparisonError}
            </div>
          )}

          <div className="pt-1">
            <button
              type="button"
              onClick={runComparison}
              disabled={!canCompare || isComparing}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed text-white transition-colors"
            >
              {isComparing ? "Comparing…" : "Compare pathways"}
            </button>
          </div>
        </div>
      )}

      {comparisonResult && (
        <div
          ref={comparisonResultsRef}
          tabIndex={-1}
          aria-label="Comparison results"
          className="space-y-4 pt-2 border-t border-gray-200 dark:border-slate-600 min-w-0 outline-none scroll-mt-4"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
              Comparison results
            </h4>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">View:</span>
              <div className="flex rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-gray-50 dark:bg-slate-700">
                <button
                  type="button"
                  onClick={() => setComparisonViewMode("graph")}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${comparisonViewMode === "graph"
                    ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                >
                  Network graph
                </button>
                <button
                  type="button"
                  onClick={() => setComparisonViewMode("statistics")}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 dark:border-slate-600 ${comparisonViewMode === "statistics"
                    ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                >
                  Statistics
                </button>
              </div>
            </div>
          </div>
          {comparisonViewMode === "graph" ? (
            <ComparisonNetworkGraph result={comparisonResult} />
          ) : (
            <ComparisonStatistics result={comparisonResult} />
          )}
        </div>
      )}

      {pathwayOptions.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No pathways available. Select a pathway type and load pathways first.
        </p>
      )}
    </div>
  );
};
